// Pre-tenant onboarding intake, reached at /onboard/:token.
//
// The prospect has no account — the link IS the credential (see server/onboarding.ts).
// Everything autosaves on every step transition, because the single worst outcome
// here is a produce wholesaler filling in twelve steps, closing the tab, and losing
// it all. Progress survives a closed tab, a dead phone, and a day off.
//
// Conditional steps mean a typical tenant answers ~15 of these, not all of them.
//
// The fit check (2026-10) — accounting tool, delivery days by town/route, prices
// by type of customer, selling units, paperwork, "one thing you'd change" — exists
// because Ralph Pitt needed every one of those and we found out mid-onboarding.
// Every answer is optional; the super-admin view turns them into "Before the
// session" flags (client/src/lib/onboardingFlags.ts).

import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { formatCutoffTime, getDayName } from "@shared/periods";
import { Check, Loader2, Upload, X, AlertCircle } from "lucide-react";

type Answers = Record<string, any>;
type UploadedFile = { id: number; kind: string; filename: string; sizeBytes: number };

const DAY_INDEXES = [0, 1, 2, 3, 4, 5, 6];

// Mirrors order_window_configs.cutoffHour / cutoffMinute defaults. Kept in one
// place because the TimePicker, the preview sentence, and the seeded answer must
// agree — if they drift, a tenant sees one cutoff and we record another.
const DEFAULT_CUTOFF_HOUR = 17;
const DEFAULT_CUTOFF_MINUTE = 0;

// ---------------------------------------------------------------- helpers

function Choice({
  value, options, onChange, columns = 1,
}: {
  value: any;
  options: Array<{ value: string; label: string; hint?: string }>;
  onChange: (v: string) => void;
  columns?: number;
}) {
  return (
    <div className={`grid gap-3 ${columns === 2 ? "sm:grid-cols-2" : ""}`}>
      {options.map((o) => {
        const selected = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className={`w-full rounded-xl border-2 px-4 py-3 text-left transition ${
              selected
                ? "border-green-600 bg-green-50"
                : "border-gray-200 bg-white hover:border-gray-300"
            }`}
          >
            <div className="flex items-center justify-between gap-3">
              <span className="font-medium text-gray-900">{o.label}</span>
              {selected && <Check className="h-5 w-5 shrink-0 text-green-600" />}
            </div>
            {o.hint && <p className="mt-1 text-sm text-gray-500">{o.hint}</p>}
          </button>
        );
      })}
    </div>
  );
}

function DayPicker({ value, onChange }: { value: number[]; onChange: (d: number[]) => void }) {
  const set = new Set(value ?? []);
  // Grid, not flex-wrap: index.css has a global `@media (max-width: 640px)` rule
  // that forces `width: 100% !important` on anything with `bg-white`, which
  // stretched every unselected day button to full width on phones while the
  // selected one (bg-green-50) stayed small. Grid items take their width from the
  // track, so that rule becomes a no-op here instead of a layout bug.
  return (
    <div className="grid grid-cols-7 gap-1.5">
      {DAY_INDEXES.map((d) => {
        const on = set.has(d);
        return (
          <button
            key={d}
            type="button"
            onClick={() => {
              const next = new Set(set);
              on ? next.delete(d) : next.add(d);
              onChange(Array.from(next).sort((x, y) => x - y));
            }}
            className={`h-12 w-full rounded-lg border-2 text-sm font-medium transition ${
              on ? "border-green-600 bg-green-50 text-green-800" : "border-gray-200 bg-white text-gray-600"
            }`}
          >
            {getDayName(d)}
          </button>
        );
      })}
    </div>
  );
}

function TimePicker({
  hour, minute, onChange,
}: {
  hour: number;
  minute: number;
  onChange: (h: number, m: number) => void;
}) {
  // Stored as 24h; presented as 12h + meridiem, which is how everyone in this
  // industry actually says it.
  const meridiem = hour >= 12 ? "PM" : "AM";
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  const setFrom = (nh12: number, nm: number, nmer: string) => {
    let h = nh12 % 12;
    if (nmer === "PM") h += 12;
    onChange(h, nm);
  };
  // w-full inside a grid track, not an intrinsic width: the global mobile rule in
  // index.css forces `width: 100% !important` on `.bg-white`, which would blow each
  // select out to the full row. Grid tracks set the size, so the rule is a no-op.
  const cls = "h-12 w-full rounded-lg border-2 border-gray-200 bg-white px-3 text-base";
  return (
    <div className="grid grid-cols-[1fr_auto_1fr_1fr] items-center gap-2">
      <select className={cls} value={h12} onChange={(e) => setFrom(Number(e.target.value), minute, meridiem)}>
        {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
          <option key={h} value={h}>{h}</option>
        ))}
      </select>
      <span className="text-lg text-gray-400">:</span>
      <select className={cls} value={minute} onChange={(e) => setFrom(h12, Number(e.target.value), meridiem)}>
        {[0, 15, 30, 45].map((m) => (
          <option key={m} value={m}>{String(m).padStart(2, "0")}</option>
        ))}
      </select>
      <select className={cls} value={meridiem} onChange={(e) => setFrom(h12, minute, e.target.value)}>
        <option>AM</option>
        <option>PM</option>
      </select>
    </div>
  );
}

/**
 * The live example sentence. Uses the same `formatCutoffTime` / `getDayName` the
 * real scheduler uses, so the preview cannot drift from actual behaviour.
 */
function cutoffPreview(a: Answers, prefix: "delivery" | "pickup"): string | null {
  const freq = a[`${prefix}Frequency`];
  const when = a[`${prefix}CutoffWhen`];
  // Fall back to the same defaults the TimePicker displays. Without this the
  // preview stays blank for anyone who accepts the default time, which is most
  // people — the picker showed 5:00 PM while answers held nothing.
  const hour = a[`${prefix}CutoffHour`] ?? DEFAULT_CUTOFF_HOUR;
  const minute = a[`${prefix}CutoffMinute`] ?? DEFAULT_CUTOFF_MINUTE;
  if (!freq || !when) return null;

  let day: number | null = null;
  if (freq === "custom") {
    const days: number[] = a[`${prefix}Days`] ?? [];
    if (!days.length) return null;
    day = days[0];
  } else {
    day = a[`${prefix}Day`] ?? null;
  }
  if (day == null) return null;

  const cutoffDay = when === "day_before" ? (day + 6) % 7 : day;
  const freqWord =
    freq === "weekly" ? "weekly" : freq === "biweekly" ? "biweekly" : freq === "monthly" ? "monthly" : "";
  const noun = prefix === "delivery" ? "delivery" : "pickup";

  return `For ${freqWord} ${noun} on ${getDayName(day, true)}, orders must be placed by ${formatCutoffTime(hour, minute)} on ${getDayName(cutoffDay, true)}.`;
}

function FileField({
  token, kind, label, hint, files, onChanged,
}: {
  token: string;
  kind: string;
  label: string;
  hint?: string;
  files: UploadedFile[];
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const mine = files.filter((f) => f.kind === kind);

  const upload = async (file: File) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      const res = await fetch(`/api/onboarding/${token}/files`, { method: "POST", body: fd });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message ?? "Upload failed");
      onChanged();
    } catch (e: any) {
      toast({ title: "Couldn't upload that", description: e.message, variant: "destructive" });
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (id: number) => {
    await fetch(`/api/onboarding/${token}/files/${id}`, { method: "DELETE" });
    onChanged();
  };

  return (
    <div>
      <Label className="text-base">{label}</Label>
      {hint && <p className="mt-1 text-sm text-gray-500">{hint}</p>}
      <div className="mt-3 space-y-2">
        {mine.map((f) => (
          <div key={f.id} className="flex items-center justify-between rounded-lg border bg-gray-50 px-3 py-2">
            <span className="truncate text-sm text-gray-700">{f.filename}</span>
            <button type="button" onClick={() => remove(f.id)} aria-label={`Remove ${f.filename}`}>
              <X className="h-4 w-4 text-gray-400 hover:text-gray-700" />
            </button>
          </div>
        ))}
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
        />
        <Button type="button" variant="outline" disabled={busy} onClick={() => inputRef.current?.click()}>
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
          {mine.length ? "Add another" : "Choose a file"}
        </Button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ steps

type StepCtx = {
  a: Answers;
  set: (patch: Answers) => void;
  token: string;
  files: UploadedFile[];
  refreshFiles: () => void;
};

type Step = {
  id: string;
  title: string;
  subtitle?: string;
  visible?: (a: Answers) => boolean;
  body: (c: StepCtx) => JSX.Element;
};

const YES_NO = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

// The integration only works with QuickBooks Online. Ralph Pitt found that out
// mid-onboarding (2026-09) — their bookkeeper is on Desktop, and nothing in the
// form had asked. An unanswered `qbProduct` still counts as "might be Online" so
// a prospect who skips the question is asked about connecting, same as before.
const mayBeOnQbo = (a: Answers) => !a.qbProduct || a.qbProduct === "online";
const wantsQbo = (a: Answers) => mayBeOnQbo(a) && a.qbIntegrate === "yes";
const offersDelivery = (a: Answers) => a.fulfillmentType === "delivery" || a.fulfillmentType === "both";

const STEPS: Step[] = [
  {
    id: "branding",
    title: "Your logo",
    subtitle: "Optional — skip it and we'll sort this out later.",
    body: ({ token, files, refreshFiles }) => (
      <FileField
        token={token}
        kind="logo"
        label="Company logo"
        hint="Highest resolution you have. A photo of your sign works too."
        files={files}
        onChanged={refreshFiles}
      />
    ),
  },
  {
    id: "staff",
    title: "Who on your team needs a login?",
    subtitle: "Anyone who'll check orders, do procurement, or handle invoicing.",
    body: ({ a, set }) => {
      const count = a.staffCount ?? 1;
      const staff: Array<{ name: string; email: string }> = a.staff ?? [];
      const rows = Array.from({ length: count }, (_, i) => staff[i] ?? { name: "", email: "" });
      const update = (i: number, key: "name" | "email", v: string) => {
        const next = [...rows];
        next[i] = { ...next[i], [key]: v };
        set({ staff: next });
      };
      return (
        <div className="space-y-6">
          <div>
            <Label className="text-base">How many logins?</Label>
            {/* Grid, not flex-wrap — same reason as DayPicker: the global mobile rule
                in index.css forces `width: 100% !important` on `.bg-white`, so the
                unselected buttons stretched while the selected one (bg-green-50) did
                not, making the button change size when tapped. */}
            <div className="mt-3 grid grid-cols-5 gap-2">
              {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  type="button"
                  // Pad, don't slice — `rows` was built from the previous count, so
                  // slicing to a larger n leaves the saved array short and the
                  // recorded staff list disagrees with the stated count.
                  onClick={() =>
                    set({
                      staffCount: n,
                      staff: Array.from({ length: n }, (_, i) => rows[i] ?? { name: "", email: "" }),
                    })
                  }
                  className={`h-12 w-full rounded-lg border-2 font-medium ${
                    count === n ? "border-green-600 bg-green-50 text-green-800" : "border-gray-200 bg-white text-gray-600"
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-4">
            {rows.map((r, i) => (
              <div key={i} className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label className="text-sm">Name</Label>
                  <Input value={r.name} onChange={(e) => update(i, "name", e.target.value)} className="mt-1 h-12 bg-white" />
                </div>
                <div>
                  <Label className="text-sm">Email</Label>
                  <Input
                    type="email"
                    value={r.email}
                    onChange={(e) => update(i, "email", e.target.value)}
                    className="mt-1 h-12 bg-white"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    },
  },
  {
    id: "qbProduct",
    title: "What do you use for accounting?",
    subtitle: "FreshOrder connects to QuickBooks Online. Anything else works differently, so it helps to know up front.",
    body: ({ a, set }) => (
      <div className="space-y-4">
        <Choice
          value={a.qbProduct}
          // Moving off "online" drops a stale "connect it" answer — otherwise the
          // QuickBooks detail step would still show for someone who just told us
          // they're on Desktop, and the review page would say both things at once.
          // Same for the "which one?" box once they move off "other".
          onChange={(v) =>
            set({
              qbProduct: v,
              qbIntegrate: v === "online" ? a.qbIntegrate : undefined,
              qbProductOther: v === "other" ? a.qbProductOther : undefined,
            })
          }
          options={[
            { value: "online", label: "QuickBooks Online", hint: "We log in through a browser at qbo.intuit.com." },
            { value: "desktop", label: "QuickBooks Desktop", hint: "Installed on a computer in the office." },
            { value: "sage", label: "Sage" },
            { value: "other", label: "Something else" },
            { value: "none", label: "We don't use accounting software" },
          ]}
        />
        {a.qbProduct === "other" && (
          <div>
            <Label className="text-base">Which one?</Label>
            <Input
              className="mt-2 h-12 bg-white"
              placeholder="e.g. Xero, FreshBooks, our own spreadsheet"
              value={a.qbProductOther ?? ""}
              onChange={(e) => set({ qbProductOther: e.target.value })}
            />
          </div>
        )}
        {/* Anything but QuickBooks Online means invoicing stays manual. Said up front
            so it's settled before the setup session, not discovered during it. */}
        {(a.qbProduct === "desktop" || a.qbProduct === "sage" || a.qbProduct === "other") && (
          <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <div className="space-y-2">
              <p>
                Only QuickBooks Online connects. With anything else, invoicing stays manual — we'll
                talk it through.
              </p>
              {a.qbProduct === "desktop" && (
                <p>
                  If you're on Desktop, the usual path is moving to QuickBooks Online, which your
                  accountant may already be planning now that Intuit is winding Desktop down.
                </p>
              )}
              <p>Everything else in FreshOrder works either way.</p>
            </div>
          </div>
        )}
      </div>
    ),
  },
  {
    id: "quickbooks",
    title: "Do you want to connect QuickBooks Online?",
    subtitle: "We can push approved orders straight through as invoices.",
    visible: mayBeOnQbo,
    body: ({ a, set }) => (
      <Choice
        value={a.qbIntegrate}
        onChange={(v) => set({ qbIntegrate: v })}
        options={[
          { value: "yes", label: "Yes", hint: "We'll connect it together on our call." },
          { value: "no", label: "No", hint: "No problem — we'll set your clients up by hand." },
          { value: "unsure", label: "Not sure yet", hint: "We can decide when we talk." },
        ]}
      />
    ),
  },
  {
    id: "clientList",
    title: "Your client list",
    subtitle: "Whatever you have — a spreadsheet, an export, or just typed out.",
    visible: (a) => !wantsQbo(a),
    body: ({ a, set, token, files, refreshFiles }) => (
      <div className="space-y-6">
        <FileField
          token={token}
          kind="client_list"
          label="Upload your client list"
          hint="Business name, who places the order, their mobile, and email if you have it."
          files={files}
          onChanged={refreshFiles}
        />
        <div>
          <Label className="text-base">Or type them here</Label>
          <Textarea
            className="mt-2 min-h-[140px] bg-white"
            placeholder="One per line — business name, contact, phone"
            value={a.clientListText ?? ""}
            onChange={(e) => set({ clientListText: e.target.value })}
          />
        </div>
      </div>
    ),
  },
  {
    id: "clientStructure",
    title: "How your clients are set up",
    body: ({ a, set }) => (
      <div className="space-y-8">
        <div>
          <Label className="text-base">
            Does anyone order on behalf of more than one account?
          </Label>
          <p className="mb-3 mt-1 text-sm text-gray-500">
            Same person ordering for different stores or locations, each getting its own invoice.
          </p>
          <Choice value={a.multiAccountContacts} onChange={(v) => set({ multiAccountContacts: v })} options={YES_NO} columns={2} />
        </div>
        <div>
          <Label className="text-base">Does any one client have several people who order?</Label>
          <p className="mb-3 mt-1 text-sm text-gray-500">
            One company, more than one person placing orders on its behalf.
          </p>
          <Choice value={a.multiContactAccounts} onChange={(v) => set({ multiContactAccounts: v })} options={YES_NO} columns={2} />
        </div>
      </div>
    ),
  },
  {
    id: "qbDetail",
    title: "How you use QuickBooks",
    visible: wantsQbo,
    body: ({ a, set }) => (
      <div className="space-y-8">
        <div>
          <Label className="text-base">Invoice numbers</Label>
          <p className="mb-3 mt-1 text-sm text-gray-500">
            Does QuickBooks number your invoices automatically, or do you type them in?
          </p>
          <Choice
            value={a.qbInvoiceNumbering}
            onChange={(v) => set({ qbInvoiceNumbering: v })}
            options={[
              { value: "auto", label: "QuickBooks numbers them" },
              { value: "manual", label: "We enter them ourselves" },
              { value: "unsure", label: "Not sure" },
            ]}
          />
        </div>
        <div>
          <Label className="text-base">
            Does anyone on your team key invoices into QuickBooks by hand?
          </Label>
          <p className="mb-3 mt-1 text-sm text-gray-500">
            Separate from anything FreshOrder would send.
          </p>
          <Choice value={a.qbManualEntry} onChange={(v) => set({ qbManualEntry: v })} options={YES_NO} columns={2} />
        </div>
        <div>
          <Label className="text-base">
            Do you use the Products &amp; Services list in QuickBooks?
          </Label>
          <p className="mb-3 mt-1 text-sm text-gray-500">To track sales by item.</p>
          <Choice value={a.qbProductsServices} onChange={(v) => set({ qbProductsServices: v })} options={YES_NO} columns={2} />
        </div>
      </div>
    ),
  },
  {
    id: "productList",
    title: "Your product list",
    subtitle: "The big one. Anything you already have beats starting from scratch.",
    visible: (a) => !wantsQbo(a) || a.qbProductsServices === "no",
    body: ({ a, set, token, files, refreshFiles }) => (
      <div className="space-y-6">
        <FileField
          token={token}
          kind="product_list"
          label="Upload your product or price list"
          hint="Spreadsheet, PDF, or a photo of a printed sheet — all fine."
          files={files}
          onChanged={refreshFiles}
        />
        <div>
          <Label className="text-base">Or paste it here</Label>
          <Textarea
            className="mt-2 min-h-[140px] bg-white"
            placeholder="Item, unit (case / lb / bunch), price"
            value={a.productListText ?? ""}
            onChange={(e) => set({ productListText: e.target.value })}
          />
        </div>
        <p className="text-sm text-gray-500">
          Don't have it handy? Skip this — we'll chase it up after.
        </p>
      </div>
    ),
  },
  {
    id: "presentation",
    title: "How your menu should look",
    body: ({ a, set }) => (
      <div className="space-y-8">
        <div>
          <Label className="text-base">Do you have product photos you'd want to use?</Label>
          <p className="mb-3 mt-1 text-sm text-gray-500">
            If not, we'll get you setup with images from our content library for now.
          </p>
          <Choice value={a.hasPhotos} onChange={(v) => set({ hasPhotos: v })} options={YES_NO} columns={2} />
        </div>
        <div>
          <Label className="text-base">Do you want to show prices to your customers?</Label>
          <Choice
            value={a.showPrices}
            onChange={(v) => set({ showPrices: v })}
            options={[
              { value: "yes", label: "Yes" },
              { value: "no", label: "No" },
              { value: "unsure", label: "Not sure yet" },
            ]}
          />
        </div>
      </div>
    ),
  },
  {
    id: "pricing",
    title: "How you price and sell",
    body: ({ a, set }) => (
      <div className="space-y-8">
        <div>
          <Label className="text-base">Do all your customers pay the same prices?</Label>
          <p className="mb-3 mt-1 text-sm text-gray-500">
            Or do some types of customers — grocers and restaurants, say — get different prices?
          </p>
          <Choice
            value={a.pricingVaries}
            onChange={(v) => set({ pricingVaries: v })}
            options={[
              { value: "same", label: "Everyone pays the same" },
              {
                value: "by_type",
                label: "Some types of customers pay different prices",
                hint: "For example, one price list for grocers and another for restaurants.",
              },
              { value: "by_customer", label: "Some customers have their own prices" },
            ]}
          />
        </div>
        <div>
          <Label className="text-base">How do you sell?</Label>
          <div className="mt-3">
            <Choice
              value={a.sellingUnits}
              onChange={(v) => set({ sellingUnits: v })}
              options={[
                { value: "case", label: "By the case" },
                { value: "each", label: "By the each" },
                { value: "weight", label: "By weight" },
                { value: "mix", label: "A mix", hint: "Like cases for some customers and singles for others." },
              ]}
              columns={2}
            />
          </div>
        </div>
      </div>
    ),
  },
  {
    id: "fulfillment",
    title: "How do orders reach your customers?",
    body: ({ a, set }) => (
      <Choice
        value={a.fulfillmentType}
        onChange={(v) => set({ fulfillmentType: v })}
        options={[
          { value: "delivery", label: "Delivery only" },
          { value: "pickup", label: "Pickup only" },
          { value: "both", label: "Both" },
        ]}
      />
    ),
  },
  {
    id: "deliveryArea",
    title: "Do all your customers get the same delivery days?",
    subtitle: "Or does it depend on their town or route?",
    visible: offersDelivery,
    body: ({ a, set }) => (
      <Choice
        value={a.deliveryDaysVary}
        onChange={(v) => set({ deliveryDaysVary: v })}
        options={[
          { value: "same", label: "Same days for everyone" },
          {
            value: "by_area",
            label: "It depends on their town or route",
            hint: "For example, the north route gets Tuesdays and town gets Monday and Thursday.",
          },
          { value: "by_customer", label: "It depends on the customer" },
        ]}
      />
    ),
  },
  {
    id: "deliverySchedule",
    title: "Your delivery schedule",
    visible: offersDelivery,
    body: ({ a, set }) => (
      <div className="space-y-6">
        {(a.deliveryDaysVary === "by_area" || a.deliveryDaysVary === "by_customer") && (
          <p className="rounded-lg bg-white px-4 py-3 text-sm text-gray-600">
            Put in every day you deliver, and the cutoff. We'll sort out who gets which day on our call.
          </p>
        )}
        <ScheduleBlock a={a} set={set} prefix="delivery" />
      </div>
    ),
  },
  {
    id: "pickupSame",
    title: "Pickup schedule",
    visible: (a) => a.fulfillmentType === "pickup" || a.fulfillmentType === "both",
    body: ({ a, set }) =>
      a.fulfillmentType === "pickup" ? (
        <ScheduleBlock a={a} set={set} prefix="pickup" />
      ) : (
        <Choice
          value={a.pickupSameAsDelivery}
          onChange={(v) => set({ pickupSameAsDelivery: v })}
          options={[
            { value: "same", label: "Same days and cutoff as delivery" },
            { value: "different", label: "Different — I'll set them out separately" },
          ]}
        />
      ),
  },
  {
    id: "pickupSchedule",
    title: "Your pickup schedule",
    visible: (a) => a.fulfillmentType === "both" && a.pickupSameAsDelivery === "different",
    body: ({ a, set }) => <ScheduleBlock a={a} set={set} prefix="pickup" />,
  },
  {
    id: "perClient",
    title: "Does everyone get both options?",
    visible: (a) => a.fulfillmentType === "both",
    body: ({ a, set }) => (
      <Choice
        value={a.fulfillmentVariesByClient}
        onChange={(v) => set({ fulfillmentVariesByClient: v })}
        options={[
          { value: "all", label: "All clients can do either" },
          { value: "varies", label: "It varies by client", hint: "We'll go through who gets what on our call." },
        ]}
      />
    ),
  },
  {
    id: "paperwork",
    title: "Your paperwork",
    subtitle: "Optional — skip it if you're happy with ours.",
    body: ({ a, set, token, files, refreshFiles }) => (
      <div className="space-y-6">
        <div>
          <Label className="text-base">
            Is there paperwork that needs to look a certain way?
          </Label>
          <p className="mt-1 text-sm text-gray-500">
            Pick sheets, driver sheets, invoices — tell us what matters about them.
          </p>
          <Textarea
            className="mt-3 min-h-[120px] bg-white"
            placeholder="e.g. Drivers need one sheet per route, in stop order."
            value={a.paperworkNotes ?? ""}
            onChange={(e) => set({ paperworkNotes: e.target.value })}
          />
        </div>
        <FileField
          token={token}
          kind="paperwork"
          label="Upload an example if you can"
          hint="A PDF, a spreadsheet, or a photo of a printed one — all fine."
          files={files}
          onChanged={refreshFiles}
        />
      </div>
    ),
  },
  {
    id: "anything",
    title: "Last thing",
    subtitle: "Both optional, but the most useful boxes on the form.",
    body: ({ a, set }) => (
      <div className="space-y-8">
        <div>
          <Label className="text-base">
            If you could change one thing about how orders reach you today, what would it be?
          </Label>
          <Textarea
            className="mt-3 min-h-[120px] bg-white"
            value={a.oneChange ?? ""}
            onChange={(e) => set({ oneChange: e.target.value })}
          />
        </div>
        <div>
          <Label className="text-base">Anything else?</Label>
          <Textarea
            className="mt-3 min-h-[120px] bg-white"
            placeholder="Anything you've tried that didn't work? Anything you think software will get wrong?"
            value={a.anythingElse ?? ""}
            onChange={(e) => set({ anythingElse: e.target.value })}
          />
        </div>
      </div>
    ),
  },
];

function ScheduleBlock({
  a, set, prefix,
}: {
  a: Answers;
  set: (p: Answers) => void;
  prefix: "delivery" | "pickup";
}) {
  const freq = a[`${prefix}Frequency`];
  const preview = cutoffPreview(a, prefix);
  const noun = prefix === "delivery" ? "Deliveries" : "Pickups";

  return (
    <div className="space-y-8">
      <div>
        <Label className="text-base">How often?</Label>
        <div className="mt-3">
          <Choice
            value={freq}
            // Seed the cutoff defaults alongside the frequency. The TimePicker only
            // shows a default; it never writes one, so without this a tenant who
            // accepts 5:00 PM submits with no cutoff recorded at all.
            onChange={(v) =>
              set({
                [`${prefix}Frequency`]: v,
                [`${prefix}CutoffHour`]: a[`${prefix}CutoffHour`] ?? DEFAULT_CUTOFF_HOUR,
                [`${prefix}CutoffMinute`]: a[`${prefix}CutoffMinute`] ?? DEFAULT_CUTOFF_MINUTE,
              })
            }
            options={[
              { value: "weekly", label: "Weekly" },
              { value: "biweekly", label: "Every two weeks" },
              { value: "monthly", label: "Monthly" },
              { value: "custom", label: "Certain days of the week" },
            ]}
            columns={2}
          />
        </div>
      </div>

      {freq === "custom" && (
        <div>
          <Label className="text-base">Which days?</Label>
          <div className="mt-3">
            <DayPicker
              value={a[`${prefix}Days`] ?? []}
              onChange={(d) => set({ [`${prefix}Days`]: d })}
            />
          </div>
        </div>
      )}

      {(freq === "weekly" || freq === "biweekly" || freq === "monthly") && (
        <div>
          <Label className="text-base">Which day are {noun.toLowerCase()}?</Label>
          <div className="mt-3">
            <DayPicker
              value={a[`${prefix}Day`] != null ? [a[`${prefix}Day`]] : []}
              onChange={(d) => set({ [`${prefix}Day`]: d[d.length - 1] ?? null })}
            />
          </div>
        </div>
      )}

      {freq && (
        <div>
          <Label className="text-base">When's the cutoff?</Label>
          <p className="mb-3 mt-1 text-sm text-gray-500">
            After this, orders roll to the next available {prefix} day.
          </p>
          {/* A dropdown rather than two stacked buttons: it composes with the time
              picker directly below into one readable sentence ("The day before,
              at 5:00 PM") and saves a row of vertical space on a step that is
              already dense. w-full is explicit because the global mobile rule in
              index.css would force it anyway — here that is the intent, not a bug. */}
          <div className="space-y-3">
            <select
              className="h-12 w-full rounded-lg border-2 border-gray-200 bg-white px-3 text-base"
              value={a[`${prefix}CutoffWhen`] ?? ""}
              onChange={(e) => set({ [`${prefix}CutoffWhen`]: e.target.value })}
            >
              <option value="" disabled>Choose when…</option>
              <option value="day_before">The day before</option>
              <option value="same_day">Same day</option>
            </select>
            <TimePicker
              hour={a[`${prefix}CutoffHour`] ?? DEFAULT_CUTOFF_HOUR}
              minute={a[`${prefix}CutoffMinute`] ?? DEFAULT_CUTOFF_MINUTE}
              onChange={(h, m) => set({ [`${prefix}CutoffHour`]: h, [`${prefix}CutoffMinute`]: m })}
            />
          </div>
          {preview && (
            <p className="mt-4 rounded-lg bg-gray-50 px-4 py-3 text-sm italic text-gray-600">
              {preview}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------- screen

export default function OnboardingWizard() {
  const { token } = useParams<{ token: string }>();
  const { toast } = useToast();
  const [answers, setAnswers] = useState<Answers>({});
  const [stepIdx, setStepIdx] = useState(0);
  const [done, setDone] = useState(false);
  const loaded = useRef(false);

  const { data, isLoading, isError, refetch } = useQuery<{
    prospectName: string;
    answers: Answers;
    currentStep: number;
    status: string;
    files: UploadedFile[];
  }>({
    queryKey: [`/api/onboarding/${token}`],
    queryFn: async () => {
      const res = await fetch(`/api/onboarding/${token}`);
      if (!res.ok) throw new Error("invalid link");
      return res.json();
    },
    retry: false,
  });

  // Hydrate once — later refetches (after a file upload) must not clobber
  // in-progress typing.
  useEffect(() => {
    if (data && !loaded.current) {
      loaded.current = true;
      setAnswers(data.answers ?? {});
      setStepIdx(data.currentStep ?? 0);
      if (data.status === "submitted") setDone(true);
    }
  }, [data]);

  const visibleSteps = useMemo(() => STEPS.filter((s) => !s.visible || s.visible(answers)), [answers]);
  const step = visibleSteps[Math.min(stepIdx, visibleSteps.length - 1)];
  const isLast = stepIdx >= visibleSteps.length - 1;

  const save = useMutation({
    mutationFn: async (payload: { answers: Answers; currentStep: number }) => {
      const res = await fetch(`/api/onboarding/${token}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("save failed");
    },
  });

  const submit = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/onboarding/${token}/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ answers, currentStep: stepIdx }),
      });
      if (!res.ok) throw new Error("submit failed");
    },
    onSuccess: () => setDone(true),
    onError: () =>
      toast({
        title: "Couldn't send that",
        description: "Your answers are saved — try again in a moment.",
        variant: "destructive",
      }),
  });

  const set = (patch: Answers) => setAnswers((prev) => ({ ...prev, ...patch }));

  // Save on every change, debounced — not just on step transitions. Saving only
  // when navigating loses whatever was entered on the current step if the tab is
  // closed, which is exactly what the footer promises won't happen. The typing
  // case (textareas) is why this is debounced rather than immediate.
  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(() => {
    if (!loaded.current || done) return;
    const t = setTimeout(() => {
      saveRef.current.mutate({ answers, currentStep: stepIdx });
    }, 700);
    return () => clearTimeout(t);
  }, [answers, stepIdx, done]);

  const go = (delta: number) => {
    const next = Math.max(0, Math.min(visibleSteps.length - 1, stepIdx + delta));
    save.mutate({ answers, currentStep: next });
    setStepIdx(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="mx-auto flex min-h-screen max-w-md items-center px-6">
        <Card className="w-full p-8 text-center">
          <AlertCircle className="mx-auto h-10 w-10 text-gray-400" />
          <h1 className="mt-4 text-xl font-semibold text-gray-900">This link isn't valid</h1>
          <p className="mt-2 text-gray-600">
            It may have expired or been mistyped. Get in touch and we'll send a fresh one.
          </p>
        </Card>
      </div>
    );
  }

  if (done) {
    return (
      <div className="mx-auto flex min-h-screen max-w-md items-center px-6">
        <Card className="w-full p-8 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
            <Check className="h-6 w-6 text-green-600" />
          </div>
          <h1 className="mt-4 text-xl font-semibold text-gray-900">Got it — thanks.</h1>
          <p className="mt-2 text-gray-600">
            We'll start building your account from this. If anything's unclear we'll text you rather
            than send another form. Next step is our setup session.
          </p>
          {/* Submitting is a signal, not a lock. Someone who remembers their cutoff is
              wrong on the drive home should be able to fix it here rather than email
              about it — so the form stays reachable and keeps autosaving. */}
          <p className="mt-6 text-sm text-gray-500">
            Thought of something else? You can still change your answers.
          </p>
          <Button
            variant="outline"
            className="mt-3 h-12 w-full"
            onClick={() => {
              setDone(false);
              setStepIdx(0);
            }}
          >
            Review or change my answers
          </Button>
        </Card>
      </div>
    );
  }

  const pct = ((stepIdx + 1) / visibleSteps.length) * 100;

  return (
    <div className="min-h-screen bg-gray-50 pb-28">
      <div className="mx-auto max-w-2xl px-5 pt-8">
        <div className="mb-2 flex items-center justify-between text-sm text-gray-500">
          <span>{data?.prospectName}</span>
          <span>
            Step {stepIdx + 1} of {visibleSteps.length}
          </span>
        </div>
        <Progress value={pct} className="h-1.5" />

        {/* Step 1 only — framing the whole form is useful once, and nagging on every
            step afterwards is not. The three claims here (skippable, autosaves,
            changeable later) are each literally true, and they are the three things
            that actually reduce drop-off on a form this long. */}
        {stepIdx === 0 && (
          <p className="mt-6 rounded-lg bg-white px-4 py-3 text-[15px] leading-relaxed text-gray-600">
            This is everything I need to set up your account. About 15 minutes — skip anything
            you're unsure about, it saves as you go, and you can change your answers any time.
          </p>
        )}

        <div className="mt-8">
          <h1 className="text-2xl font-semibold text-gray-900">{step.title}</h1>
          {step.subtitle && <p className="mt-2 text-gray-600">{step.subtitle}</p>}
        </div>

        <div className="mt-8">
          {step.body({
            a: answers,
            set,
            token: token!,
            files: data?.files ?? [],
            refreshFiles: () => refetch(),
          })}
        </div>
      </div>

      <div className="fixed inset-x-0 bottom-0 border-t bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-2xl items-center justify-between gap-3 px-5 py-4">
          <Button variant="ghost" onClick={() => go(-1)} disabled={stepIdx === 0} className="h-12">
            Back
          </Button>
          <p className="hidden text-xs text-gray-400 sm:block">
            Your answers save as you go — you can close this and come back.
          </p>
          {isLast ? (
            <Button onClick={() => submit.mutate()} disabled={submit.isPending} className="h-12 px-8">
              {submit.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Send it over
            </Button>
          ) : (
            <Button onClick={() => go(1)} className="h-12 px-8">
              Continue
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
